class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.setupCustomActions();
    this.setupCarouselActions();
  }

  setupCustomActions() {
    this.micBtn = this.querySelector('.f-search-mic-btn');
    this.clearBtn = this.querySelector('.f-search-clear-btn');
    
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.close(true);
      });
    }

    if (this.micBtn) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        this.micBtn.style.display = 'none';
      } else {
        this.micBtn.addEventListener('click', this.startSpeechRecognition.bind(this));
      }
    }
  }

  startSpeechRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Tu navegador no soporta búsqueda por voz.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-CO';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      this.input.placeholder = "Escuchando...";
      this.micBtn.classList.add('is-listening');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      this.input.value = transcript;
      this.input.placeholder = "Buscar";
      this.micBtn.classList.remove('is-listening');
      this.onChange();
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      this.input.placeholder = "Buscar";
      this.micBtn.classList.remove('is-listening');
    };

    recognition.onend = () => {
      this.input.placeholder = "Buscar";
      this.micBtn.classList.remove('is-listening');
    };

    recognition.start();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // iOS Safari has a long-standing bug where touch-scrolling a position:fixed
    // container is unreliable/blocked while a text input inside it is focused
    // (keyboard open). Confirmed via screen recording: results consistently
    // froze at the same cutoff point with the keyboard up, but the page
    // scrolled fine the instant the keyboard was dismissed. Blur the input as
    // soon as the user starts dragging on the results so the keyboard closes
    // and the fixed overlay becomes scrollable again -- guarded with
    // suppressBlurClose so this programmatic blur doesn't trigger onFocusOut's
    // normal "focus left the component, close the whole search" behavior.
    this.overlayContainer = this.querySelector('.f-predictive-search-overlay-container');
    if (this.overlayContainer) {
      this.overlayContainer.addEventListener(
        'touchstart',
        (event) => {
          if (document.activeElement === this.input && event.target !== this.input) {
            this.suppressBlurClose = true;
            this.input.blur();
          }
        },
        { passive: true }
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    // Always clear the input when user focuses the search bar so it starts fresh
    this.input.value = '';
    this.searchTerm = '';
    this.removeAttribute('results');
    this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    this.querySelector('[data-predictive-search] .f-results-carousel-container')?.remove();
    this.open();
  }

  onFocusOut() {
    if (this.suppressBlurClose) {
      this.suppressBlurClose = false;
      return;
    }
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.querySelector('[data-predictive-search] .f-results-carousel-container')?.remove(); // quita los productos de la búsqueda anterior si vacías el input
    }
    
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const predictiveSearchUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[limit]=10&section_id=predictive-search`;

    fetch(predictiveSearchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setupCarouselActions();

    this.setLiveRegionResults();
    this.open();

    // Update the info row search term tag
    const termEl = this.querySelector('.f-results-search-term');
    if (termEl) termEl.textContent = this.searchTerm;
    const viewAllLink = this.querySelector('.f-results-view-all');
    if (viewAllLink) viewAllLink.href = `${routes.search_url}?q=${encodeURIComponent(this.searchTerm)}`;
  }

  setupCarouselActions() {
    const carousels = this.querySelectorAll('.f-results-carousel-container');
    
    carousels.forEach(container => {
      if (container.dataset.carouselSetup) return;
      container.dataset.carouselSetup = 'true';
      
      const track = container.querySelector('.f-results-carousel-track');
      const prevBtn = container.querySelector('.f-carousel-prev');
      const nextBtn = container.querySelector('.f-carousel-next');

      if (!track || !prevBtn || !nextBtn) return;

      const scrollAmount = track.clientWidth || 300;

      // Prevent mousedown from stealing focus from the search input, which would trigger onFocusOut and close the popup
      prevBtn.addEventListener('mousedown', (e) => e.preventDefault());
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });

      nextBtn.addEventListener('mousedown', (e) => e.preventDefault());
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Body scroll lock: plain `overflow: hidden` on <body> (what the CSS
    // `body:has(predictive-search[open])` rule uses) is well known to not reliably
    // block background scroll on iOS Safari, and :has() itself silently no-ops on
    // iOS < 16.4 anyway. Use the standard iOS-safe lock instead: fix the body in
    // place at its current scroll offset, then restore that offset on close.
    this.scrollLockY = window.scrollY;
    document.body.style.top = `-${this.scrollLockY}px`;
    document.body.classList.add('f-predictive-search-locked');
  }

  close(clearSearchTerm = true) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.searchTerm = '';
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('f-predictive-search-locked');
    document.body.style.top = '';
    if (typeof this.scrollLockY === 'number') {
      window.scrollTo(0, this.scrollLockY);
      this.scrollLockY = null;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
